/** Hooks TanStack Query del Registro de recursos. */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { buildRepos } from "@core/data/repos";
import type { AjusteRr, EstadoAjusteRr, RegistroRecursos } from "@app-types/entities";
import { otsDelContratista } from "@features/contratos/scopeContratos";
import { porcionDeOt } from "./porcionDeOt";
import { recursosEnVivo } from "./ventanaAjuste";
import { puedeTransicionarAjuste } from "./ajusteRr";

export function useRegistrosRecursos(tenantId: string) {
  return useQuery({
    queryKey: ["registrosRecursos", tenantId],
    queryFn: () => buildRepos(tenantId).registroRecursos.list(),
    staleTime: 30_000,
  });
}

/** El registro de un contratista en un día. `null` mientras no exista. */
export function useRegistroDelDia(
  tenantId: string,
  contratistaId: string | null,
  fecha: string,
) {
  return useQuery({
    queryKey: ["registrosRecursos", tenantId, contratistaId, fecha],
    queryFn: () =>
      contratistaId
        ? buildRepos(tenantId).registroRecursos.getDelDia(contratistaId, fecha)
        : Promise.resolve(null),
    enabled: !!contratistaId,
  });
}

/** Histórico de un contratista, del más reciente al más antiguo. */
export function useRegistrosDeContratista(tenantId: string, contratistaId: string | null) {
  return useQuery({
    queryKey: ["registrosRecursos", tenantId, contratistaId, "__hist__"],
    queryFn: () =>
      contratistaId
        ? buildRepos(tenantId).registroRecursos.listByContratista(contratistaId)
        : Promise.resolve([]),
    enabled: !!contratistaId,
  });
}

/**
 * Guarda el registro del día: lo crea si no existe, lo actualiza si ya está.
 *
 * La llave natural es (contratista, fecha) y el repo la resuelve, así que la
 * pantalla no tiene que saber si es alta o edición. Dos registros del mismo
 * día para el mismo contratista producirían dos verdades sobre la misma
 * jornada, y el que leyera primero ganaría.
 */
export function useGuardarRegistroRecursos(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      contratistaId: string;
      contratistaNombre?: string;
      fecha: string;
      personal: RegistroRecursos["personal"];
      maquinaria: RegistroRecursos["maquinaria"];
      vehiculos: RegistroRecursos["vehiculos"];
      herramientas: RegistroRecursos["herramientas"];
      userEmail: string;
    }) => {
      const repo = buildRepos(tenantId).registroRecursos;
      const existente = await repo.getDelDia(args.contratistaId, args.fecha);
      const cuerpo = {
        contratistaId: args.contratistaId,
        ...(args.contratistaNombre ? { contratistaNombre: args.contratistaNombre } : {}),
        fecha: args.fecha,
        personal: args.personal,
        maquinaria: args.maquinaria,
        vehiculos: args.vehiculos,
        herramientas: args.herramientas ?? [],
        updatedBy: args.userEmail,
      };
      const id = existente
        ? (await repo.update(existente.id, cuerpo, existente.version), existente.id)
        : await repo.create({ ...cuerpo, createdBy: args.userEmail } as Omit<
            RegistroRecursos,
            "id" | "createdAt" | "updatedAt" | "version"
          >);

      const propagado = await propagarARds(tenantId, args.contratistaId, args.fecha);
      return { id, ...propagado };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["registrosRecursos", tenantId] });
      // El reporte diario en borrador LEE de aquí: si no se refresca, el
      // contratista corrige sus recursos y el reporte sigue mostrando lo viejo.
      void qc.invalidateQueries({ queryKey: ["rds"] });
    },
  });
}

/**
 * Propaga el registro del día a los reportes diarios de ese contratista.
 *
 * # Por qué es automático
 *
 * Criterio del responsable: los reportes LEEN lo que quede reajustado en este
 * módulo, así que corregir aquí tiene que verse allá sin pasos extra. Sin esto,
 * arreglar un día mal digitado exigiría entrar reporte por reporte.
 *
 * # Qué reportes se tocan y cuáles no
 *
 * Se escribe en los que tienen sus recursos EN VIVO: borrador, en revisión, y
 * los aprobados con la ventana de ajuste abierta.
 *
 * Un reporte APROBADO y sin autorización NO se toca. Su firma cubre los
 * recursos: cambiarlos por detrás dejaría la firma certificando algo distinto
 * de lo que muestra el documento, y nadie se enteraría. Para esos, el PL2 abre
 * la ventana desde la pantalla del reporte — y entonces sí entran aquí.
 *
 * Devuelve el recuento para que la interfaz diga qué pasó en vez de que el
 * usuario tenga que adivinar por qué un día no cambió.
 */
async function propagarARds(
  tenantId: string,
  contratistaNombre: string,
  fecha: string,
  opts: { forzar?: boolean } = {},
): Promise<{ actualizados: number; bloqueados: string[] }> {
  const repos = buildRepos(tenantId);
  const [registro, sos] = await Promise.all([
    repos.registroRecursos.getDelDia(contratistaNombre, fecha),
    repos.so.list(),
  ]);
  if (!registro) return { actualizados: 0, bloqueados: [] };

  const mias = otsDelContratista(contratistaNombre, sos);
  let actualizados = 0;
  const bloqueados: string[] = [];

  for (const so of mias) {
    const rds = await repos.rd.listBySo(so.id);
    const rd = rds.find((r) => r.fecha === fecha);
    if (!rd) continue;

    // `forzar` viene de la APROBACIÓN del PL2: esa decisión ES la autorización
    // para tocar un reporte firmado, y queda registrada en el ajuste.
    if (!opts.forzar && !recursosEnVivo(rd)) {
      bloqueados.push(so.codigo);
      continue;
    }
    const porcion = porcionDeOt(registro, so.id);
    await repos.rd.update(rd.id, { recursos: porcion }, rd.version);

    // Si el reporte estaba APROBADO, su firma cubría los recursos viejos.
    // Cambiarlos sin re-firmar la dejaría certificando otra cosa.
    if (rd.estado === "APROBADO") {
      const post = await repos.rd.get(rd.id);
      if (post) {
        const firma = await repos.rd.calculateSignature(rd.id);
        await repos.rd.update(rd.id, { firmaSha256: firma }, post.version);
      }
    }
    actualizados++;
  }

  return { actualizados, bloqueados };
}

/**
 * Mueve el ajuste de un registro por su ciclo.
 *
 * Al APROBAR, la corrección baja a los reportes diarios de esa fecha —incluidos
 * los ya aprobados— y se recalcula su firma. Esa es la razón de ser del ciclo:
 * la aprobación del PL2 ES la autorización para tocar un documento firmado, y
 * por eso queda constancia de quién la dio.
 *
 * `semilla` cubre los días anteriores al módulo, cuyos recursos viven dentro de
 * los reportes y no existen como registro. Al SOLICITAR se materializa con
 * exactamente lo que esos reportes ya declaran —ningún valor cambia— porque sin
 * un registro el ciclo no tendría sobre qué correr, y justamente esos días son
 * los que hay que corregir.
 */
export function useAjusteRr(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      contratistaId: string;
      fecha: string;
      accion: "solicitar" | "autorizar" | "rechazar" | "enviar" | "conceptuar" | "aprobar";
      texto?: string;
      userEmail: string;
      semilla?: Pick<
        RegistroRecursos,
        "personal" | "maquinaria" | "vehiculos" | "herramientas"
      >;
    }) => {
      const repos = buildRepos(tenantId);
      let reg = await repos.registroRecursos.getDelDia(args.contratistaId, args.fecha);

      if (!reg && args.accion === "solicitar" && args.semilla) {
        const id = await repos.registroRecursos.create({
          contratistaId: args.contratistaId,
          contratistaNombre: args.contratistaId,
          fecha: args.fecha,
          personal: args.semilla.personal,
          maquinaria: args.semilla.maquinaria,
          vehiculos: args.semilla.vehiculos,
          herramientas: args.semilla.herramientas ?? [],
          createdBy: args.userEmail,
          updatedBy: args.userEmail,
        } as Omit<RegistroRecursos, "id" | "createdAt" | "updatedAt" | "version">);
        reg = await repos.registroRecursos.get(id);
      }
      if (!reg) throw new Error("No hay registro de recursos para esa fecha.");

      const now = new Date().toISOString();
      const prev = reg.ajuste;
      const texto = (args.texto ?? "").trim();
      let sig: AjusteRr;

      switch (args.accion) {
        case "solicitar":
          // El motivo es obligatorio: es la traza de POR QUÉ se corrigió, que
          // es justo lo que falta cuando una cifra no cuadra meses después.
          if (!texto) throw new Error("Indica el motivo del ajuste.");
          sig = {
            estado: "SOLICITADO",
            solicitadoPor: args.userEmail,
            solicitadoEn: now,
            motivo: texto,
          };
          break;
        case "autorizar":
          sig = exigir(prev, "AUTORIZADO", {
            autorizadoPor: args.userEmail,
            autorizadoEn: now,
          });
          break;
        case "enviar":
          sig = exigir(prev, "EN_REVISION", {});
          break;
        case "conceptuar":
          sig = exigir(prev, "EN_REVISION", {
            conceptoInt: texto,
            conceptoIntPor: args.userEmail,
            conceptoIntEn: now,
          });
          break;
        case "aprobar":
          sig = exigir(prev, "APROBADO", { aprobadoPor: args.userEmail, aprobadoEn: now });
          break;
        case "rechazar":
          sig = exigir(prev, "RECHAZADO", {
            rechazadoPor: args.userEmail,
            rechazadoEn: now,
            ...(texto ? { observacionesPl2: texto } : {}),
          });
          break;
      }

      await repos.registroRecursos.update(
        reg.id,
        { ajuste: sig, updatedBy: args.userEmail },
        reg.version,
      );

      if (args.accion === "aprobar") {
        return propagarARds(tenantId, args.contratistaId, args.fecha, { forzar: true });
      }
      return { actualizados: 0, bloqueados: [] };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["registrosRecursos", tenantId] });
      void qc.invalidateQueries({ queryKey: ["rds"] });
    },
  });
}

/** Valida la transición antes de escribirla. Sin camino, no hay cambio. */
function exigir(
  prev: AjusteRr | undefined,
  destino: EstadoAjusteRr,
  patch: Partial<AjusteRr>,
): AjusteRr {
  if (!prev) throw new Error("No hay un ajuste en curso para este registro.");
  // Conceptuar no cambia de estado: es una anotación sobre EN_REVISION.
  if (prev.estado !== destino && !puedeTransicionarAjuste(prev.estado, destino)) {
    throw new Error(`No se puede pasar de ${prev.estado} a ${destino}.`);
  }
  return { ...prev, ...patch, estado: destino };
}
